/* eslint-disable unicorn/consistent-function-scoping -- цепочка middleware как в passport-client */
/**
 * Асинхронный Redux middleware загрузки профиля пользователя из API.
 *
 * Цепочка: при `FETCH_PROFILE_START` с валидным `token` в payload выполняется
 * `fetchUserProfileRequest`, ответ нормализуется (`unwrapProfilePayload` → `mapResponseToUser`)
 * и диспатчится `FETCH_PROFILE_SUCCESS` или `FETCH_PROFILE_ERROR` с `FetchError`.
 *
 * Вспомогательные функции ниже безопасно читают поля из `Record<string, unknown>`,
 * чтобы не полагаться на форму ответа без проверок типов.
 *
 * @module store/profile/fetchProfile.middleware
 */

import { fetchUserProfileRequest } from '../../requests';
import { FetchError } from '../../requests/sendRequest';
import { isFakedEmail } from '../../utils/fakeEmails';
import type { RootAction, RootDispatch, RootState } from '../store.types';
import type { Middleware } from '../store.types';

import {
  FETCH_PROFILE_ERROR,
  FETCH_PROFILE_START,
  FETCH_PROFILE_SUCCESS,
  PROFILE_FETCH_ERROR_KEY,
} from './actionTypes';
import type { SurveyUserContacts, SurveyUserProfile, SurveyUserSocialLink } from './userProfile.types';

/**
 * Читает опциональное строковое поле объекта: если ключа нет или значение не строка — `null`.
 *
 * @param {Record<string, unknown>} record — объект ответа API
 * @param {string} key — имя поля
 * @returns {string | null} строка или `null`
 */
function readOptionalString(record: Record<string, unknown>, key: string): string | null {
  if (!Object.hasOwn(record, key)) {
    return null;
  }
  const value = record[key];
  return typeof value === 'string' ? value : null;
}

/**
 * Читает опциональное булево поле: если ключа нет или значение не `boolean` — `null`.
 *
 * @param {Record<string, unknown>} record — объект ответа API
 * @param {string} key — имя поля
 * @returns {boolean | null} булево значение или `null`
 */
function readOptionalBoolean(record: Record<string, unknown>, key: string): boolean | null {
  if (!Object.hasOwn(record, key)) {
    return null;
  }
  const value = record[key];
  return typeof value === 'boolean' ? value : null;
}

/**
 * Читает массив языков из поля `languages`: не-массив и не-строки в элементах отбрасываются.
 *
 * @param {Record<string, unknown>} record — объект профиля
 * @returns {string[] | null} массив кодов языков или `null`, если поля нет или оно не массив
 */
function readLanguages(record: Record<string, unknown>): string[] | null {
  if (!Object.hasOwn(record, 'languages')) {
    return null;
  }
  const value = record.languages;
  if (!Array.isArray(value)) {
    return null;
  }
  const out: string[] = [];
  for (const item of value) {
    if (typeof item === 'string') {
      out.push(item);
    }
  }
  return out;
}

/**
 * Собирает массив соцсвязей из `socialLinks`: пропускает элементы без `identityProvider` / `userName` или с неверными типами.
 *
 * @param {Record<string, unknown>} record — объект профиля
 * @returns {SurveyUserSocialLink[]} массив связей (пустой, если поля нет или формат неверный)
 */
function readSocialLinks(record: Record<string, unknown>): SurveyUserSocialLink[] {
  if (!Object.hasOwn(record, 'socialLinks')) {
    return [];
  }
  const value = record.socialLinks;
  if (!Array.isArray(value)) {
    return [];
  }
  const out: SurveyUserSocialLink[] = [];
  for (const item of value) {
    if (item === null || typeof item !== 'object') {
      continue;
    }
    const rec = item as Record<string, unknown>;
    if (!Object.hasOwn(rec, 'identityProvider') || !Object.hasOwn(rec, 'userName')) {
      continue;
    }
    const ip = rec.identityProvider;
    const un = rec.userName;
    if (typeof ip === 'string' && typeof un === 'string') {
      out.push({ identityProvider: ip, userName: un });
    }
  }
  return out;
}

/**
 * Читает вложенный объект `contacts` и маппит поля контактов; флаги `*Verified` приводятся к `boolean` только при значении `true`.
 *
 * @param {Record<string, unknown>} record — объект профиля
 * @returns {SurveyUserContacts | undefined} контакты или `undefined`, если блока нет или он не объект
 */
function readContacts(record: Record<string, unknown>): SurveyUserContacts | undefined {
  if (!Object.hasOwn(record, 'contacts')) {
    return undefined;
  }
  const raw = record.contacts;
  if (raw === null || typeof raw !== 'object') {
    return undefined;
  }
  const c = raw as Record<string, unknown>;
  return {
    phone: readOptionalString(c, 'phone'),
    phoneVerified: readOptionalBoolean(c, 'phoneVerified') === true,
    whatsApp: readOptionalString(c, 'whatsApp'),
    whatsAppVerified: readOptionalBoolean(c, 'whatsAppVerified') === true,
    telegram: readOptionalString(c, 'telegram'),
    telegramVerified: readOptionalBoolean(c, 'telegramVerified') === true,
    viber: readOptionalString(c, 'viber'),
    viberVerified: readOptionalBoolean(c, 'viberVerified') === true,
    skype: readOptionalString(c, 'skype'),
    skypeVerified: readOptionalBoolean(c, 'skypeVerified') === true,
    icq: readOptionalString(c, 'icq'),
    icqVerified: readOptionalBoolean(c, 'icqVerified') === true,
  };
}

/**
 * Читает числовой timestamp запроса на удаление аккаунта (`deleteRequestTimestamp`).
 *
 * @param {Record<string, unknown>} record — объект профиля
 * @returns {number | undefined} число или `undefined`, если поля нет или тип не `number`
 */
function readDeleteTimestamp(record: Record<string, unknown>): number | undefined {
  if (!Object.hasOwn(record, 'deleteRequestTimestamp')) {
    return undefined;
  }
  const value = record.deleteRequestTimestamp;
  return typeof value === 'number' ? value : undefined;
}

/**
 * Преобразует распарсенный объект профиля API в модель `SurveyUserProfile` для store.
 * Помечает email как сгенерированный через `isFakedEmail`, если строка непустая.
 *
 * @param {Record<string, unknown>} fetched — объект профиля (уже без обёртки `data`, если она была снята выше)
 * @returns {SurveyUserProfile} нормализованный профиль
 */
function mapResponseToUser(fetched: Record<string, unknown>): SurveyUserProfile {
  const emailRaw = readOptionalString(fetched, 'email');
  let isFaked = false;
  if (emailRaw !== null && emailRaw !== '') {
    isFaked = isFakedEmail(emailRaw);
  }

  const totpVal = readOptionalBoolean(fetched, 'totp');

  return {
    id: readOptionalString(fetched, 'id'),
    username: readOptionalString(fetched, 'username'),
    firstName: readOptionalString(fetched, 'firstName'),
    lastName: readOptionalString(fetched, 'lastName'),
    middleName: readOptionalString(fetched, 'middleName'),
    totp: totpVal,
    country: readOptionalString(fetched, 'country'),
    birthday: readOptionalString(fetched, 'birthday'),
    locale: readOptionalString(fetched, 'locale'),
    languages: readLanguages(fetched),
    email: emailRaw,
    emailVerified: readOptionalBoolean(fetched, 'emailVerified'),
    isFakedEmail: isFaked,
    socialLinks: readSocialLinks(fetched),
    phone: readOptionalString(fetched, 'phone'),
    phoneVerified: readOptionalBoolean(fetched, 'phoneVerified'),
    contacts: readContacts(fetched),
    deleteRequestTimestamp: readDeleteTimestamp(fetched),
  };
}

/**
 * Извлекает полезную нагрузку профиля: при наличии вложенного `data`-объекта возвращает его, иначе — корень ответа.
 * Не-объекты и `null` дают `null`.
 *
 * @param {unknown} raw — тело ответа HTTP (`response.data`)
 * @returns {Record<string, unknown> | null} объект для `mapResponseToUser` или `null`
 */
function unwrapProfilePayload(raw: unknown): Record<string, unknown> | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const root = raw as Record<string, unknown>;
  if (Object.hasOwn(root, 'data') && root.data !== null && typeof root.data === 'object') {
    return root.data as Record<string, unknown>;
  }
  return root;
}

/**
 * Redux middleware: перехватывает только `FETCH_PROFILE_START` с непустым строковым `payload.token`.
 * Остальные действия пробрасываются без изменений. После валидации токена сначала пропускает исходный `START`
 * (чтобы редьюсеры могли выставить состояние загрузки), затем асинхронно запрашивает профиль и диспатчит
 * успех или ошибку. Ошибки сети и невалидный ответ упаковываются в `FetchError` под `PROFILE_FETCH_ERROR_KEY`.
 */
export const fetchProfileMiddleware: Middleware<unknown, RootState, RootDispatch> =
  (_api) => (next) => async (action: RootAction) => {
    // Не наша команда — стандартная передача по цепочке
    if (action.type !== FETCH_PROFILE_START) {
      return next(action);
    }

    const payload = action.payload;
    // Без объекта payload или без строкового token middleware не запускает запрос
    if (payload === null || typeof payload !== 'object' || !Object.hasOwn(payload, 'token')) {
      return next(action);
    }
    const tokenRecord = payload as Record<string, unknown>;
    const tokenVal = tokenRecord.token;
    if (typeof tokenVal !== 'string') {
      return next(action);
    }
    const token = tokenVal;

    // Пропускаем FETCH_PROFILE_START дальше (loading и т.д.) до await
    next(action);

    try {
      const response = await fetchUserProfileRequest(token);
      const raw = response.data;
      const fetched = unwrapProfilePayload(raw);

      if (response.ok && fetched !== null) {
        const userData = mapResponseToUser(fetched);
        return next({ type: FETCH_PROFILE_SUCCESS, payload: userData });
      }
      // HTTP не ок или тело не разобралось в объект профиля
      return next({
        type: FETCH_PROFILE_ERROR,
        payload: {
          [PROFILE_FETCH_ERROR_KEY]: new FetchError({
            message: 'Invalid profile response',
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
            payload: response,
          }),
        },
      });
    } catch (error) {
      // Сетевые и прочие исключения приводим к FetchError для единообразия в редьюсере
      const err =
        error instanceof FetchError
          ? error
          : new FetchError({
              message: error instanceof Error ? error.message : String(error),
              status: 0,
              statusText: '',
              headers: {},
              payload: error,
            });
      return next({
        type: FETCH_PROFILE_ERROR,
        payload: { [PROFILE_FETCH_ERROR_KEY]: err },
      });
    }
  };
